/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { useEffect, useRef } from 'react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import {
  HeadingSize,
  HEADING_SIZE_MAP,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { SectionName } from '../../../observability';
import { unsecureID } from '../../../utils';
import { InViewTracking } from '../../InViewTracking/InViewTracking';

import { BuilderComponentProps } from '../../../builder';

export interface EventAgendaSectionProps extends BuilderComponentProps {
  eventID: string;
  heading?: string;
  headingSize?: HeadingSize;
  headingAlignment?: 'center' | 'left';
}

const Styles = labelStyles('EventAgendaSection', {
  root: css({
    maxWidth: '1170px',
    margin: '40px auto',
    padding: '0 20px',
  }),

  heading: (
    headingSize: EventAgendaSectionProps['headingSize'] = 'medium',
    headingAlignment: EventAgendaSectionProps['headingAlignment'] = 'left',
  ) =>
    css({
      ...Typography[HEADING_SIZE_MAP[headingSize].mobile],
      color: Colors.neutral800,
      margin: '0 auto 0.75em',
      textAlign: headingAlignment,

      [MEDIA_MIN_WIDTH.small]: {
        ...Typography[HEADING_SIZE_MAP[headingSize].tablet],
      },

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography[HEADING_SIZE_MAP[headingSize].desktop],
      },
    }),

  embedContainer: css({
    height: '1200px',
    borderRadius: '10px',
    overflow: 'hidden',
  }),
});

export function EventAgendaSection(props: EventAgendaSectionProps) {
  const embedContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const embedContainer = embedContainerRef.current;

    if (embedContainer) {
      const script = document.createElement('script');
      script.setAttribute(
        'src',
        'https://www.accelevents.com/e/js/embedScript-vti76d0k3j5ne6j7j8k3n4.min.js',
      );
      script.setAttribute('event-data', props.eventID);
      script.setAttribute('embedType', 'agenda');
      script.setAttribute(
        'embed',
        'https://www.accelevents.com/u/embedScript/agenda',
      );
      script.setAttribute('widgetFor', 'EVENT');
      script.setAttribute('id', unsecureID());

      embedContainer.appendChild(script);
      return () => {
        embedContainer.removeChild(script);
      };
    }
  }, [props.eventID]);

  return (
    <InViewTracking
      css={Styles.root}
      layerName={props.builderBlock?.layerName}
      sectionName={SectionName.EventAgendaSection}
    >
      <h2 css={Styles.heading(props.headingSize, props.headingAlignment)}>
        {props.heading}
      </h2>
      <div css={Styles.embedContainer} ref={embedContainerRef} />
    </InViewTracking>
  );
}
